import moment from 'moment';
import { Button } from 'react-bootstrap';
import { useSearchParams } from 'react-router-dom';
import { StatsFilters as StatsFiltersType } from '../../../../domain/statsFilters';
import { useLoggedInUser } from '../../../users/LoggedInUserProvider';
import { buildSearchParamsFromStatsFilters } from './buildSearchParamsFromStatsFilters';

type StatsQuickFiltersProps = {
    filters: StatsFiltersType;
}

const now = moment();
const currentMonth = now.month() + 1;
const currentYear = now.year();
const currentQuarter = Math.ceil(currentMonth / 3);
const currentHalfYear = Math.ceil(currentMonth / 6);

const pastMonthDate = now.clone().subtract(1, 'months');
const pastMonth = pastMonthDate.month() + 1;
const pastMonthYear = pastMonthDate.year();

const nextMonthDate = now.clone().add(1, 'months');
const nextMonth = nextMonthDate.month() + 1;
const nextMonthYear = nextMonthDate.year();

export const StatsQuickFilters = ({ filters }: StatsQuickFiltersProps) => {
    const { loggedInUser } = useLoggedInUser();
    const [, setSearchParams] = useSearchParams();

    const isCurrentDispatcher = filters.creator?.id === loggedInUser.sub;

    const isCurrentMonth =
        filters.timePeriodSelector === 'single-month' &&
        filters.singleMonthYear?.month === currentMonth &&
        filters.singleMonthYear?.year === currentYear;

    const isPastMonth =
        filters.timePeriodSelector === 'single-month' &&
        filters.singleMonthYear?.month === pastMonth &&
        filters.singleMonthYear?.year === pastMonthYear;

    const isNextMonth =
        filters.timePeriodSelector === 'single-month' &&
        filters.singleMonthYear?.month === nextMonth &&
        filters.singleMonthYear?.year === nextMonthYear;

    const isCurrentQuarter =
        filters.timePeriodSelector === 'quarter' &&
        filters.quarter?.quarter === currentQuarter &&
        filters.quarter?.year === currentYear;

    const isCurrentHalfYear =
        filters.timePeriodSelector === 'half-year' &&
        filters.halfYear?.halfYear === currentHalfYear &&
        filters.halfYear?.year === currentYear;

    const isCurrentYearFilter =
        filters.timePeriodSelector === 'year' &&
        filters.yearFilter?.year === currentYear;

    const handleYourStatsClick = () => {
        let updatedFilters: StatsFiltersType;
        if (isCurrentDispatcher) {
            updatedFilters = {
                ...filters,
                creator: undefined,
            };
        } else {
            updatedFilters = {
                ...filters,
                creator: {
                    id: loggedInUser.sub,
                },
            };
        }
        setSearchParams(buildSearchParamsFromStatsFilters(updatedFilters));
    };

    const handleCurrentMonthClick = () => {
        let updatedFilters: StatsFiltersType;
        if (isCurrentMonth) {
            updatedFilters = {
                ...filters,
                timePeriodSelector: 'single-month',
                singleMonthYear: undefined,
            };
        } else {
            updatedFilters = {
                ...filters,
                timePeriodSelector: 'single-month',
                singleMonthYear: {
                    month: currentMonth,
                    year: currentYear,
                },
            };
        }
        setSearchParams(buildSearchParamsFromStatsFilters(updatedFilters));
    };

    const handlePastMonthClick = () => {
        let updatedFilters: StatsFiltersType;
        if (isPastMonth) {
            updatedFilters = {
                ...filters,
                timePeriodSelector: 'single-month',
                singleMonthYear: undefined,
            };
        } else {
            updatedFilters = {
                ...filters,
                timePeriodSelector: 'single-month',
                singleMonthYear: {
                    month: pastMonth,
                    year: pastMonthYear,
                },
            };
        }
        setSearchParams(buildSearchParamsFromStatsFilters(updatedFilters));
    };

    const handleNextMonthClick = () => {
        let updatedFilters: StatsFiltersType;
        if (isNextMonth) {
            updatedFilters = {
                ...filters,
                timePeriodSelector: 'single-month',
                singleMonthYear: undefined,
            };
        } else {
            updatedFilters = {
                ...filters,
                timePeriodSelector: 'single-month',
                singleMonthYear: {
                    month: nextMonth,
                    year: nextMonthYear,
                },
            };
        }
        setSearchParams(buildSearchParamsFromStatsFilters(updatedFilters));
    };

    const handleCurrentQuarterClick = () => {
        let updatedFilters: StatsFiltersType;
        if (isCurrentQuarter) {
            updatedFilters = {
                ...filters,
                timePeriodSelector: 'quarter',
                quarter: undefined,
            };
        } else {
            updatedFilters = {
                ...filters,
                timePeriodSelector: 'quarter',
                quarter: {
                    quarter: currentQuarter,
                    year: currentYear,
                },
            };
        }
        setSearchParams(buildSearchParamsFromStatsFilters(updatedFilters));
    };

    const handleCurrentHalfYearClick = () => {
        let updatedFilters: StatsFiltersType;
        if (isCurrentHalfYear) {
            updatedFilters = {
                ...filters,
                timePeriodSelector: 'half-year',
                halfYear: undefined,
            };
        } else {
            updatedFilters = {
                ...filters,
                timePeriodSelector: 'half-year',
                halfYear: {
                    halfYear: currentHalfYear,
                    year: currentYear,
                },
            };
        }
        setSearchParams(buildSearchParamsFromStatsFilters(updatedFilters));
    };

    const handleCurrentYearClick = () => {
        let updatedFilters: StatsFiltersType;
        if (isCurrentYearFilter) {
            updatedFilters = {
                ...filters,
                timePeriodSelector: 'year',
                yearFilter: undefined,
            };
        } else {
            updatedFilters = {
                ...filters,
                timePeriodSelector: 'year',
                yearFilter: {
                    year: currentYear,
                },
            };
        }
        setSearchParams(buildSearchParamsFromStatsFilters(updatedFilters));
    };

    return (
        <ul className="d-flex flex-row list-unstyled">
            <li className="me-2">
                <Button onClick={handleYourStatsClick} variant={isCurrentDispatcher ? 'primary' : 'outline-primary'}>
                    Twoje statystyki
                </Button>
            </li>
            <li className="me-2">
                <Button onClick={handleCurrentMonthClick} variant={isCurrentMonth ? 'primary' : 'outline-primary'}>
                    Bieżący miesiąc
                </Button>
            </li>
            <li className="me-2">
                <Button onClick={handlePastMonthClick} variant={isPastMonth ? 'primary' : 'outline-primary'}>
                    Poprzedni miesiąc
                </Button>
            </li>
            <li className="me-2">
                <Button onClick={handleNextMonthClick} variant={isNextMonth ? 'primary' : 'outline-primary'}>
                    Następny miesiąc
                </Button>
            </li>
            <li className="me-2">
                <Button onClick={handleCurrentQuarterClick} variant={isCurrentQuarter ? 'primary' : 'outline-primary'}>
                    Bieżący kwartał
                </Button>
            </li>
            <li className="me-2">
                <Button onClick={handleCurrentHalfYearClick} variant={isCurrentHalfYear ? 'primary' : 'outline-primary'}>
                    Bieżące półrocze
                </Button>
            </li>
            <li>
                <Button onClick={handleCurrentYearClick} variant={isCurrentYearFilter ? 'primary' : 'outline-primary'}>
                    Bieżący rok
                </Button>
            </li>
        </ul>
    );
};
